// SPDX-License-Identifier: MIT
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import { outCubic, outExpo } from '../core/ease.ts';
import { clamp, smoothstep } from '../core/math.ts';
import { parseStat } from '../kit/text.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { roundRect } from '../type.ts';
export { parseStat };
function formatStat(s: NonNullable<ReturnType<typeof parseStat>>, k: number, grouping: boolean): string {
    const v = s.n * k;
    const text = grouping
        ? v.toLocaleString('en-US', { minimumFractionDigits: s.decimals, maximumFractionDigits: s.decimals })
        : v.toFixed(s.decimals);
    return `${s.prefix}${text}${s.suffix}`;
}
/** Up to three counters that count up on staggered beats, each with a filling meter. */
export default defineScene({
    id: 'stat-counter',
    version: '0.1.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        items: z
            .array(z.object({
            value: z.string().check(z.maxLength(16)),
            label: z.string().check(z.maxLength(40)),
            meter: z.optional(z.number().check(z.gte(0)).check(z.lte(1))),
        })).check(z.minLength(1)).check(z.maxLength(3)),
    }),
    glyphs: (p) => p.items.map((i) => i.value + i.label).join('') + '0123456789.,',
    motion: (_p, env) => (env.localBeat < 2.5 ? 0.5 : 0.15),
    audioEvents: (p) => p.items.map((_, i) => ({ beat: 0.25 + i * 0.5, sfx: 'blip' as const })),
    draw(ctx, props, env) {
        const { colors } = env.brand;
        const b = env.localBeat;
        ctx.fillStyle = colors.ink;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        const n = props.items.length;
        const cardW = 500;
        const gap = 60;
        const x0 = (DESIGN_W - (n * cardW + (n - 1) * gap)) / 2;
        const y = DESIGN_H / 2 - 190;
        props.items.forEach((item, i) => {
            const start = 0.25 + i * 0.5;
            const enter = outCubic(clamp((b - start) / 0.5));
            if (enter <= 0)
                return;
            const count = outExpo(clamp((b - start) / 1.6));
            const x = x0 + i * (cardW + gap);
            ctx.save();
            ctx.globalAlpha = enter;
            ctx.translate(0, 40 * (1 - enter));
            roundRect(ctx, x, y, cardW, 380, 28);
            ctx.fillStyle = rgba(colors.paper, 0.06);
            ctx.fill();
            ctx.strokeStyle = rgba(colors.primary, 0.6);
            ctx.lineWidth = 2;
            ctx.stroke();
            const stat = parseStat(item.value);
            const text = stat ? formatStat(stat, count, item.value.includes(',')) : item.value;
            ctx.font = font(env.brand, 700, 112, 'mono');
            ctx.fillStyle = colors.paper;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.fillText(text, x + 44, y + 170);
            env.report?.text(`value-${i}`, [x + 44, y + 70, cardW - 88, 110], enter);
            ctx.font = font(env.brand, 500, 32);
            ctx.fillStyle = rgba(colors.paper, 0.7);
            ctx.fillText(item.label, x + 44, y + 240);
            env.report?.text(`label-${i}`, [x + 44, y + 210, cardW - 88, 40], enter);
            const meter = (item.meter ?? 0.8) * count;
            ctx.fillStyle = rgba(colors.paper, 0.12);
            ctx.fillRect(x + 44, y + 300, cardW - 88, 10);
            ctx.fillStyle = colors.accent;
            ctx.fillRect(x + 44, y + 300, (cardW - 88) * meter, 10);
            ctx.restore();
        });
        // Beat-synced baseline pulse.
        const pulse = 1 - smoothstep(0, 0.35, b % 1);
        ctx.fillStyle = rgba(colors.primary, 0.25 + 0.5 * pulse);
        ctx.fillRect(0, DESIGN_H - 120, DESIGN_W, 2);
    },
});
